import { useState, useMemo, useCallback } from 'react'
import { Plus, Trash2, Search, Pencil, Check, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/utils/cn'
import type { StoreModelMemberRead } from '@/types'

interface ManualGroup {
  name: string
  store_ids: Set<string>
}

interface ManualGroupingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  members: StoreModelMemberRead[]
  loading?: boolean
  onSave: (groups: { name: string; store_ids: string[] }[]) => void
  initialGroups?: { name: string; store_ids: string[] }[]
}

const UNASSIGNED = '__unassigned__'

/** Generate the next alphabetic name (A, B, C, ..., Z, AA, AB, ...) not already used. */
function nextAlphaName(existing: string[]): string {
  const used = new Set(existing)
  const toAlpha = (n: number): string => {
    let result = ''
    let num = n
    while (num >= 0) {
      result = String.fromCharCode(65 + (num % 26)) + result
      num = Math.floor(num / 26) - 1
    }
    return result
  }
  let i = 0
  while (used.has(toAlpha(i))) i++
  return toAlpha(i)
}

export function ManualGroupingDialog({
  open,
  onOpenChange,
  members,
  loading,
  onSave,
  initialGroups,
}: ManualGroupingDialogProps) {
  const [groups, setGroups] = useState<ManualGroup[]>(() =>
    initialGroups?.length
      ? initialGroups.map(g => ({
          name: g.name,
          store_ids: new Set(g.store_ids),
        }))
      : [{ name: 'A', store_ids: new Set<string>() }]
  )
  const [selectedGroupIdx, setSelectedGroupIdx] = useState(0)
  const [search, setSearch] = useState('')
  const [filterGroup, setFilterGroup] = useState<string>('all')
  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const [editName, setEditName] = useState('')

  // Build store_id → group index map
  const storeGroupMap = useMemo(() => {
    const map = new Map<string, number>()
    groups.forEach((g, idx) => {
      g.store_ids.forEach(id => map.set(id, idx))
    })
    return map
  }, [groups])

  // Filter and search members
  const filteredMembers = useMemo(() => {
    let result = members

    if (filterGroup === UNASSIGNED) {
      result = result.filter(m => !storeGroupMap.has(m.store_id))
    } else if (filterGroup !== 'all') {
      const idx = Number(filterGroup)
      result = result.filter(m => storeGroupMap.get(m.store_id) === idx)
    }

    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        m =>
          m.store_number.toLowerCase().includes(q) ||
          m.store_name.toLowerCase().includes(q) ||
          (m.region?.toLowerCase().includes(q) ?? false)
      )
    }

    return result
  }, [members, filterGroup, search, storeGroupMap])

  const unassignedCount = useMemo(
    () => members.filter(m => !storeGroupMap.has(m.store_id)).length,
    [members, storeGroupMap]
  )

  const addGroup = useCallback(() => {
    const name = nextAlphaName(groups.map(g => g.name))
    setGroups(prev => [...prev, { name, store_ids: new Set() }])
    setSelectedGroupIdx(groups.length)
  }, [groups])

  const removeGroup = useCallback(
    (idx: number) => {
      if (groups.length <= 1) return
      setGroups(prev => prev.filter((_, i) => i !== idx))
      setSelectedGroupIdx(prev => (prev >= idx ? Math.max(0, prev - 1) : prev))
    },
    [groups.length]
  )

  const startRename = (idx: number) => {
    setEditingIdx(idx)
    setEditName(groups[idx].name)
  }

  const confirmRename = () => {
    if (editingIdx == null || !editName.trim()) return
    setGroups(prev =>
      prev.map((g, i) =>
        i === editingIdx ? { ...g, name: editName.trim() } : g
      )
    )
    setEditingIdx(null)
  }

  const assignStore = useCallback(
    (storeId: string, groupIdx: number | null) => {
      setGroups(prev =>
        prev.map((g, i) => {
          const ids = new Set(g.store_ids)
          if (groupIdx === i) {
            ids.add(storeId)
          } else {
            ids.delete(storeId)
          }
          return { ...g, store_ids: ids }
        })
      )
    },
    []
  )

  const assignBulk = useCallback((storeIds: string[], groupIdx: number) => {
    setGroups(prev =>
      prev.map((g, i) => {
        const ids = new Set(g.store_ids)
        for (const sid of storeIds) {
          if (i === groupIdx) ids.add(sid)
          else ids.delete(sid)
        }
        return { ...g, store_ids: ids }
      })
    )
  }, [])

  const handleSave = () => {
    const result = groups
      .filter(g => g.store_ids.size > 0)
      .map(g => ({ name: g.name, store_ids: Array.from(g.store_ids) }))
    onSave(result)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>Configure Manual Groups</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 overflow-hidden border-t border-border mt-4">
          {/* Left panel: Groups */}
          <div className="w-64 border-r border-border flex flex-col overflow-hidden">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <span className="text-sm font-medium">Groups</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 cursor-pointer"
                onClick={addGroup}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="flex flex-col p-1">
                {groups.map((g, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'flex items-center gap-1 px-3 py-2 rounded-md cursor-pointer text-sm transition-colors',
                      selectedGroupIdx === idx
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-muted/50'
                    )}
                    onClick={() => setSelectedGroupIdx(idx)}
                  >
                    {editingIdx === idx ? (
                      <div className="flex items-center gap-1 flex-1">
                        <Input
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') confirmRename()
                            if (e.key === 'Escape') setEditingIdx(null)
                          }}
                          className="h-6 text-sm px-1"
                          autoFocus
                          onClick={e => e.stopPropagation()}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 cursor-pointer"
                          onClick={e => {
                            e.stopPropagation()
                            confirmRename()
                          }}
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 cursor-pointer"
                          onClick={e => {
                            e.stopPropagation()
                            setEditingIdx(null)
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <span className="flex-1 truncate">{g.name}</span>
                        <Badge variant="neutral" className="text-xs px-1.5">
                          {g.store_ids.size}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 cursor-pointer opacity-0 group-hover:opacity-100"
                          onClick={e => {
                            e.stopPropagation()
                            startRename(idx)
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        {groups.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 cursor-pointer text-muted-foreground hover:text-destructive"
                            onClick={e => {
                              e.stopPropagation()
                              removeGroup(idx)
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Right panel: Store list */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-3 p-3 border-b border-border">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search stores..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>

              <Select value={filterGroup} onValueChange={setFilterGroup}>
                <SelectTrigger className="w-[180px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    All stores ({members.length})
                  </SelectItem>
                  <SelectItem value={UNASSIGNED}>
                    Unassigned ({unassignedCount})
                  </SelectItem>
                  {groups.map((g, idx) => (
                    <SelectItem key={idx} value={String(idx)}>
                      {g.name} ({g.store_ids.size})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Bulk assign filtered to selected group */}
              {filterGroup === UNASSIGNED && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs cursor-pointer"
                  onClick={() =>
                    assignBulk(
                      filteredMembers.map(m => m.store_id),
                      selectedGroupIdx
                    )
                  }
                >
                  Assign all to {groups[selectedGroupIdx]?.name}
                </Button>
              )}
            </div>

            {/* Store list */}
            <div className="flex-1 overflow-y-auto">
              <div className="divide-y divide-border">
                {loading && (
                  <p className="p-6 text-sm text-muted-foreground text-center">
                    Loading stores...
                  </p>
                )}
                {!loading && filteredMembers.length === 0 && (
                  <p className="p-6 text-sm text-muted-foreground text-center">
                    No stores found
                  </p>
                )}
                {filteredMembers.map(member => {
                  const groupIdx = storeGroupMap.get(member.store_id)
                  return (
                    <div
                      key={member.store_id}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-muted/30"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {member.store_number}
                          </span>
                          <span className="text-sm text-muted-foreground truncate">
                            {member.store_name}
                          </span>
                        </div>
                        {member.region && (
                          <span className="text-xs text-muted-foreground">
                            {member.region}
                          </span>
                        )}
                      </div>
                      <Select
                        value={groupIdx != null ? String(groupIdx) : UNASSIGNED}
                        onValueChange={v =>
                          assignStore(
                            member.store_id,
                            v === UNASSIGNED ? null : Number(v)
                          )
                        }
                      >
                        <SelectTrigger
                          className={cn(
                            'w-[150px] h-7 text-xs',
                            groupIdx == null && 'text-muted-foreground'
                          )}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                          {groups.map((g, idx) => (
                            <SelectItem key={idx} value={String(idx)}>
                              {g.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with summary */}
        <DialogFooter className="px-6 py-4 border-t border-border flex-row items-center">
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            {groups.map((g, idx) => (
              <Badge
                key={idx}
                variant={g.store_ids.size > 0 ? 'info' : 'neutral'}
                className="text-xs"
              >
                {g.name}: {g.store_ids.size}
              </Badge>
            ))}
            {unassignedCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                Unassigned: {unassignedCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="cursor-pointer"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button className="cursor-pointer" onClick={handleSave}>
              Save Groups
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
